import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, Building2, Check, Mail, MessageSquare, Receipt, Save,
  ShieldCheck, Trash2, X,
} from "lucide-react";

import { useAuth } from "../contexts/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

/**
 * Settings.
 *
 * These lived in four unrelated places — retention in a dialog inside Trash,
 * registration on the attendees page, billing under documents, and claims
 * settings nowhere at all. Nobody could answer "where do I change that?"
 * without already knowing.
 *
 * Grouped by the question being answered rather than by which API happens to
 * store the value.
 */

function Section({ title, description, icon: Icon, children, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-5 space-y-4">
        <div>
          <h2 className="font-semibold text-foreground flex items-center gap-1.5">
            <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            {title}
          </h2>
          {description && (
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {description}
            </p>
          )}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

export default function SettingsPage() {
  const navigate = useNavigate();
  const { authAxios, isAdmin, isSubAdmin } = useAuth();
  const canEdit = isAdmin || isSubAdmin;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [channels, setChannels] = useState(null);
  const [health, setHealth] = useState(null);
  const [retention, setRetention] = useState("30");
  const [claims, setClaims] = useState({ claims_open: true, pre_approval_threshold: "" });

  const load = useCallback(async () => {
    try {
      const api = authAxios();
      const [s, c] = await Promise.all([
        api.get("/settings"),
        api.get("/settings/channels").catch(() => ({ data: null })),
      ]);
      setRetention(String(s.data?.trash_retention_days ?? 30));
      setClaims({
        claims_open: s.data?.claims_open !== false,
        pre_approval_threshold: s.data?.pre_approval_threshold_cents
          ? (s.data.pre_approval_threshold_cents / 100).toFixed(2) : "",
      });
      setChannels(c.data);
      if (canEdit) {
        const h = await api.get("/health/detail").catch(() => ({ data: null }));
        setHealth(h.data);
      }
    } catch {
      toast.error("Could not load settings");
    } finally {
      setLoading(false);
    }
  }, [authAxios, canEdit]);

  useEffect(() => { load(); }, [load]);

  const saveRetention = async () => {
    setSaving(true);
    try {
      const api = authAxios();
      await api.put("/settings", { trash_retention_days: Number(retention) || 30 });
      toast.success("Retention saved");
    } catch {
      toast.error("Could not save retention");
    } finally {
      setSaving(false);
    }
  };

  const saveClaims = async (overrides = {}) => {
    const next = { ...claims, ...overrides };
    setSaving(true);
    try {
      const api = authAxios();
      await api.put("/claims/settings", {
        claims_open: next.claims_open,
        pre_approval_threshold: next.pre_approval_threshold || null,
      });
      setClaims(next);
      toast.success("Reimbursement settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8 w-full space-y-3">
        <div className="h-20 bg-muted rounded-xl animate-pulse" />
        <div className="h-64 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 w-full space-y-5 pb-24">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Everything configurable, in one place
        </p>
      </div>

      {/* Whether messages are actually going out is the question people ask
          most often, and it was previously only answerable from server logs. */}
      <Section title="Notifications" icon={MessageSquare} testId="settings-channels"
               description="Which channels are configured. Missing keys are why
                            confirmation emails stop, far more often than a bug.">
        <div className="flex flex-wrap gap-2">
          {channels ? (
            Object.entries(channels).map(([name, on]) => (
              <Badge key={name} variant="secondary"
                     className={on ? "bg-emerald-500/10 text-emerald-600"
                                   : "bg-muted text-muted-foreground"}
                     data-testid={`channel-${name}`}>
                {on ? <Check className="h-3 w-3 mr-1" aria-hidden="true" />
                    : <X className="h-3 w-3 mr-1" aria-hidden="true" />}
                {name === "sms" ? "SMS" : name === "whatsapp" ? "WhatsApp" : "Email"}
              </Badge>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">Not available.</p>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Configured in <span className="font-mono">backend/.env</span> on the server,
          not here — keys do not belong in a web form.
        </p>
      </Section>

      <Section title="Reimbursements" icon={Receipt} testId="settings-claims"
               description="Whether volunteers can submit claims, and when a claim
                            deserves a second look.">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-foreground">
              {claims.claims_open ? "Claims are open" : "Claims are closed"}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {claims.claims_open
                ? "Volunteers can submit expenses through the claim link."
                : "Nobody can submit a new claim."}
            </p>
          </div>
          {canEdit && (
            <Button size="sm" className="h-11 shrink-0"
                    variant={claims.claims_open ? "outline" : "default"}
                    onClick={() => saveClaims({ claims_open: !claims.claims_open })}
                    data-testid="toggle-claims">
              {claims.claims_open ? "Close" : "Open"}
            </Button>
          )}
        </div>

        {canEdit && (
          <div className="space-y-1.5">
            <Label htmlFor="threshold">Pre-approval limit (optional)</Label>
            <div className="flex gap-2">
              <Input id="threshold" className="h-11 font-mono" inputMode="decimal"
                     placeholder="Leave blank for none"
                     value={claims.pre_approval_threshold}
                     onChange={(e) => setClaims({ ...claims,
                       pre_approval_threshold: e.target.value })}
                     data-testid="claims-threshold" />
              <Button className="h-11 gap-1.5" disabled={saving}
                      onClick={() => saveClaims()} data-testid="save-claims">
                <Save className="h-4 w-4" aria-hidden="true" />
                Save
              </Button>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Claims above this are <strong>flagged for the approver, never blocked</strong>.
              The money is already spent by the time anyone sees the claim, so refusing it
              would only penalise the volunteer.
            </p>
          </div>
        )}
      </Section>

      <Section title="Data retention" icon={Trash2} testId="settings-retention"
               description="How long deleted items stay in the bin before they are
                            permanently removed on startup.">
        {canEdit ? (
          <div className="flex gap-2 items-end">
            <div className="space-y-1.5 flex-1">
              <Label htmlFor="retention">Days in the bin</Label>
              <Input id="retention" className="h-11" inputMode="numeric" value={retention}
                     onChange={(e) => setRetention(e.target.value)}
                     data-testid="retention-days" />
            </div>
            <Button className="h-11 gap-1.5" disabled={saving} onClick={saveRetention}
                    data-testid="save-retention">
              <Save className="h-4 w-4" aria-hidden="true" />
              Save
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{retention} days</p>
        )}
      </Section>

      {/* Attendee data is the one place this app holds information about the
          public, including allergies. Saying so where settings live makes it
          harder to forget. */}
      <Section title="Attendee data" icon={ShieldCheck} testId="settings-privacy"
               description="What the app holds about people who register, and what
                            they can do about it.">
        <ul className="text-xs text-muted-foreground space-y-1.5 leading-relaxed">
          <li>• Name, email, optional phone, party size and dietary requirements</li>
          <li>• <strong className="text-foreground">Dietary and allergy notes are
            health-related</strong> and are shown to whoever runs the event</li>
          <li>• Attendees can withdraw their own registration from their ticket page</li>
          <li>• Records are kept until the event is deleted and its retention window passes</li>
        </ul>
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3
                        flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0"
                         aria-hidden="true" />
          <p className="text-xs text-amber-700 dark:text-amber-500 leading-relaxed">
            The registration form does not yet carry a privacy notice. If you collect
            allergy information from the public, one is worth adding before you print
            QR codes.
          </p>
        </div>
      </Section>

      {canEdit && (
        <Section title="Billing details" icon={Building2} testId="settings-billing"
                 description="Your organisation's details and each vendor's, as they
                              appear on documents.">
          <Button variant="outline" className="h-11"
                  onClick={() => navigate("/documents/settings")}
                  data-testid="goto-billing">
            Open billing details
          </Button>
        </Section>
      )}

      {canEdit && health && (
        <Section title="System" icon={Mail} testId="settings-system"
                 description="A quick look at whether anything is wrong.">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Status</p>
              <p className={health.status === "ok"
                ? "text-emerald-600 font-medium" : "text-destructive font-medium"}
                 data-testid="system-status">
                {health.status === "ok" ? "Healthy" : "Degraded"}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Uptime</p>
              <p className="text-foreground">
                {Math.floor((health.uptime_seconds || 0) / 3600)}h
              </p>
            </div>
            {health.storage?.disk && (
              <div>
                <p className="text-xs text-muted-foreground">Disk free</p>
                <p className="text-foreground">{health.storage.disk.free_gb} GB</p>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground">Database</p>
              <p className="text-foreground">
                {health.database?.ok ? `${health.database.ms} ms` : "Unavailable"}
              </p>
            </div>
          </div>
        </Section>
      )}
    </div>
  );
}
